import styled from "@emotion/styled";
import { LightMode, Variables } from "@repo/skapa";
import { useTranslation } from "@repo/translation";
import { DATE_FORMAT, zIndex } from "@repo/ui";
import { VisuallyHidden } from "@repo/ui/src/components/VisuallyHidden";
import ReactDatePicker from "react-datepicker";

import { DatePickerInput } from "./DatePicker";

interface InlineDatePickerProps {
  onChange: (start?: Date, end?: Date) => void;
  value: (Date | undefined)[];
  id: string;
  label?: string | string[];
  allowFutureDates?: boolean;
}

export const DatePickerStyleContainer = styled.div<{ border?: string }>`
  cursor: pointer;

  div {
    border: 0px;
  }

  .react-datepicker {
    border: ${({ border }) => (border ? border : "0px;")};

    padding: ${Variables.space75};
  }

  .react-datepicker-popper {
    z-index: ${zIndex.datepicker};
  }

  .react-datepicker__navigation {
    top: ${Variables.space75};
  }

  .react-datepicker,
  .react-datepicker__day-names,
  .react-datepicker-wrapper,
  .react-datepicker__input-container,
  .react-datepicker__month-container,
  .react-datepicker__input-container input {
    width: 100%;
  }

  .react-datepicker__triangle::after {
    border-bottom-color: #fff !important;
  }
  .react-datepicker-popper[data-placement^="bottom"]
    .react-datepicker__triangle {
    color: #fff;
    fill: #fff;
  }

  .react-datepicker__day-names,
  .react-datepicker__week {
    display: flex;
    justify-content: space-between;
  }

  .react-datepicker__day-names {
    font-size: ${Variables.fontSize50};
    margin-top: 8px;
  }

  .react-datepicker__week {
    font-size: ${Variables.fontSize75};
  }

  .react-datepicker__month {
    margin: 8px 0 0 0;
  }

  .react-datepicker__header {
    background-color: ${LightMode.colourNeutral1};
    padding: 0px;
    text-align: center;
  }

  .react-datepicker__day--in-range,
  .react-datepicker__day--in-selecting-range,
  .react-datepicker__day--keyboard-selected,
  .react-datepicker__day--selected {
    color: black;
    background-color: ${LightMode.colourStaticIkeaBrandYellow};
  }

  .react-datepicker__navigation-icon::before {
    border-color: black;
  }
  .react-datepicker__triangle::after,
  .react-datepicker__triangle::before {
    left: -${Variables.space125} !important;
  }

  .react-datepicker__time-list-item:focus-visible {
    outline: ${Variables.thicknessThick} solid;
  }

  .react-datepicker__day:focus-visible,
  .react-datepicker__month-text:focus-visible,
  .react-datepicker__quarter-text:focus-visible,
  .react-datepicker__year-text:focus-visible,
  .react-datepicker__navigation:focus-visible,
  .react-datepicker__current-month:focus-visible,
  .react-datepicker__header select:focus-visible {
    outline: ${Variables.thicknessThick} solid ${LightMode.colourTextAndIcon1};
    border-radius: ${Variables.radiusS};
  }

  .react-datepicker__day:focus-visible,
  .react-datepicker__month-text:focus-visible,
  .react-datepicker__quarter-text:focus-visible,
  .react-datepicker__year-text:focus-visible {
    outline-offset: -1px;
  }

  .react-datepicker__navigation:focus-visible {
    outline-offset: -2px;
  }

  .react-datepicker__current-month:focus-visible,
  .react-datepicker__header select:focus-visible {
    outline-offset: 2px;
  }
`;

export const InlineDatePicker = ({
  onChange,
  value,
  id,
  label,
  allowFutureDates = false,
}: InlineDatePickerProps) => {
  const { dateFnsLocale } = useTranslation();
  const normalizedLabel = Array.isArray(label) ? label[0] : label;
  const labelText = normalizedLabel ?? "From";
  return (
    <DatePickerStyleContainer role="group" aria-label={labelText}>
      <VisuallyHidden id={`fromDate-${id}-label`}>{labelText}</VisuallyHidden>
      <ReactDatePicker
        id={`fromDate-${id}`}
        customInput={<DatePickerInput label={labelText} />}
        onChange={(date) => {
          onChange(date[0] ?? undefined, date[1] ?? undefined);
        }}
        locale={dateFnsLocale}
        dateFormat={DATE_FORMAT}
        startDate={value[0]}
        endDate={value[1]}
        maxDate={allowFutureDates ? undefined : new Date()}
        selected={value[0]}
        selectsRange
        inline
        ariaLabelledBy={`fromDate-${id}-label`}
      />
    </DatePickerStyleContainer>
  );
};
